import { Component, OnInit } from '@angular/core';

import { firstValueFrom } from 'rxjs';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { ListboxModule } from 'primeng/listbox';
import { InputTextModule } from 'primeng/inputtext';

import { TranslateService } from '../../../services/translate.service';
import { ViewScopeService, Company, Customer } from '../../../services/view-scope.service';
import { RefreshService } from '../../../services/refresh.service';


@Component({
	selector: 'app-select-account',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		DialogModule,
		ButtonModule,
		DropdownModule,
		ListboxModule,
		InputTextModule,
	],
	templateUrl: './select-account.component.html',
	styleUrls: ['./select-account.component.scss'],
})
export class SelectAccountComponent implements OnInit {
	showPendingOnly = false;
	public customerFilesMap: { [customerId: number]: number } = {};
	// Data lists
	companies: Company[] = [];
	customers: Customer[] = [];

	// Selected items
	selectedCompany: Company | null = null;
	selectedCustomer: Customer | null = null;

	// Dialog state
	displayCompanyDialog = false;
	displayCustomerDialog = false;

	tempSelectedCompany: Company | null = null;
	tempSelectedCustomer: Customer | null = null;

	// Filters
	filterTextCompany = '';
	filterTextCustomer = '';
	filteredCompanies: Company[] = [];
	filteredCustomers: Customer[] = [];

	// Loading flags
	loadingCompanies = false;
	loadingCustomers = false;

	// Optional helper
	isSingleCompany = false;

	constructor(
		public translateServ: TranslateService,
		private viewScopeService: ViewScopeService,
		private refreshService: RefreshService
	) { }

	async ngOnInit(): Promise<void> {
		await this.loadCompaniesFromApi({ selectFirstAndSave: true });
		await this.loadCustomersFromApi({ selectFirstAndSave: true });
		await this.loadCustomerFilesMap();
	}

	private async loadCustomerFilesMap(): Promise<void> {
		this.viewScopeService.getPendingWorkForCustomer(false).subscribe((data: any[]) => {
			this.customerFilesMap = {};
			data.forEach(item => {
				this.customerFilesMap[item.CustomerRecID] = item.NumberOfFiles;
			});
			// Ensure every customer has a value (default 0 if missing)
			this.customers.forEach(customer => {
				if (this.customerFilesMap[customer.id] === undefined) {
					this.customerFilesMap[customer.id] = 0;
				}
			});
		});
	}

	/** --------------------------------
	 * Companies
	 * -------------------------------- */
	private async loadCompaniesFromApi(opts: { selectFirstAndSave: boolean }): Promise<void> {
		this.loadingCompanies = true;
		try {
			const companies = await firstValueFrom(this.viewScopeService.getCompanies());
			this.companies = companies.sort((a, b) => a.name.localeCompare(b.name));
			this.filteredCompanies = [...this.companies];
			this.isSingleCompany = this.companies.length === 1;

			const savedCompanyStr = localStorage.getItem('selectedCompany');
			let savedCompany: Company | null = null;
			if (savedCompanyStr) {
				try {
					savedCompany = JSON.parse(savedCompanyStr);
				} catch { }
			}

			// Security check: validate saved company exists in response
			const existingCompany = savedCompany ? this.companies.find(c => c.id === savedCompany!.id) : null;
			if (existingCompany) {
				this.selectedCompany = existingCompany;
			} else if (this.companies.length > 0) {
				this.selectedCompany = this.companies[0];
				localStorage.setItem(
					'selectedCompany',
					JSON.stringify({ id: this.selectedCompany.id, name: this.selectedCompany.name })
				);
			}
			if (this.selectedCompany) {
				this.viewScopeService.setSelectedCompany(this.selectedCompany);
			}
		} catch (err) {
			console.error('Failed to load companies:', err);
		} finally {

			this.loadingCompanies = false;

		}
	}

	private async ensureCompaniesListLoaded(): Promise<void> {
		if (this.companies.length === 0) {
			await this.loadCompaniesFromApi({ selectFirstAndSave: !this.selectedCompany });
		}
	}

	async openCompanyDialog(): Promise<void> {
		if (this.isSingleCompany && this.selectedCompany) return;

		await this.ensureCompaniesListLoaded();
		this.tempSelectedCompany = this.selectedCompany;
		this.displayCompanyDialog = true;
		this.filterTextCompany = '';
		this.filterCompanies();
	}

	closeCompanyDialog(): void {
		this.displayCompanyDialog = false;
	}

	onSelectCompany(): void {
		if (!this.tempSelectedCompany) return;

		this.selectedCompany = this.tempSelectedCompany;
		localStorage.setItem(
			'selectedCompany',
			JSON.stringify({ id: this.selectedCompany.id, name: this.selectedCompany.name })
		);
		this.viewScopeService.setSelectedCompany(this.selectedCompany);
		this.closeCompanyDialog();
		this.refreshService.triggerPageRefresh();
	}

	getCompanyText(): string {
		return this.selectedCompany
			? this.selectedCompany.name
			: this.translateServ.dictionary['SELECTING_COMP_MSG'];
	}

	filterCompanies(): void {
		const search = this.filterTextCompany.toLowerCase();
		this.filteredCompanies = !search
			? [...this.companies]
			: this.companies.filter(c => c.name.toLowerCase().includes(search));
	}

	/** --------------------------------
	 * Customers
	 * -------------------------------- */
	private async loadCustomersFromApi(opts: { selectFirstAndSave: boolean }): Promise<void> {
		this.loadingCustomers = true;
		try {
			const customers = await firstValueFrom(this.viewScopeService.getCustomers());
			this.customers = customers.sort((a, b) => a.name.localeCompare(b.name));
			this.filteredCustomers = [...this.customers];

			const savedCustomerStr = localStorage.getItem('selectedCustomer');
			let savedCustomer: Customer | null = null;
			if (savedCustomerStr) {
				try {
					savedCustomer = JSON.parse(savedCustomerStr);
				} catch { }
			}

			// Security check: validate saved customer exists in response
			const existingCustomer = savedCustomer ? this.customers.find(c => c.id === savedCustomer!.id) : null;
			if (existingCustomer) {
				this.selectedCustomer = existingCustomer;
			} else if (this.customers.length > 0) {
				this.selectedCustomer = this.customers[0];
				localStorage.setItem(
					'selectedCustomer',
					JSON.stringify({
						id: this.selectedCustomer.id,
						name: this.selectedCustomer.name,
						businessId: this.selectedCustomer.businessId,
					})
				);
			}
			if (this.selectedCustomer) {
				this.viewScopeService.setSelectedCustomer(this.selectedCustomer);
			}
		} catch (err) {
			console.error('Failed to load customers:', err);
		} finally {
			this.loadingCustomers = false;
		}
	}

	private async ensureCustomersListLoaded(): Promise<void> {
		if (this.customers.length === 0) {
			await this.loadCustomersFromApi({ selectFirstAndSave: !this.selectedCustomer });
		}
	}

	async openCustomerDialog(): Promise<void> {
		await this.ensureCustomersListLoaded();
		if (Object.keys(this.customerFilesMap).length === 0) {
			await this.loadCustomerFilesMap();
		}
		this.tempSelectedCustomer = this.selectedCustomer;
		this.displayCustomerDialog = true;
		this.filterTextCustomer = '';
		this.filterCustomers();
	}

	closeCustomerDialog(): void {
		this.displayCustomerDialog = false;
	}

	onSelectCustomer(isDialog : boolean = true): void 
	{
		if (!this.tempSelectedCustomer) return;

		this.selectedCustomer = this.tempSelectedCustomer;
		localStorage.setItem(
			'selectedCustomer',
			JSON.stringify({
				id: this.selectedCustomer.id,
				name: this.selectedCustomer.name,
				businessId: this.selectedCustomer.businessId,
			})
		);
		this.viewScopeService.setSelectedCustomer(this.selectedCustomer);
		if(isDialog) this.closeCustomerDialog();
		this.refreshService.triggerPageRefresh();
	}

	getCustomerText(): string {
		return this.selectedCustomer
			? `${this.selectedCustomer.name} (${this.selectedCustomer.businessId})`
			: this.translateServ.dictionary['SELECTING_CUSTOMER_MSG'];
	}

	filterCustomers(): void {
		const search = this.filterTextCustomer.toLowerCase();
		let baseList = [...this.customers];
		if (this.showPendingOnly) {
			baseList = baseList.filter(c => this.customerFilesMap[c.id] > 0);
		}
		this.filteredCustomers = !search
			? baseList
			: baseList.filter(
				c =>
					c.name.toLowerCase().includes(search) ||
					c.businessId.toLowerCase().includes(search)
			);
	}

	toggleShowPendingOnly(): void {
		this.showPendingOnly = !this.showPendingOnly;
		this.filterCustomers();
	}

	public async forcedCustomerSelection(forcedId: number): Promise<void> 
	{
		await this.ensureCustomersListLoaded();
		this.tempSelectedCustomer = this.customers[this.findCustomerIndexById(forcedId)];
		this.onSelectCustomer(false);
	}

	private findCustomerIndexById(id: number): number {
		return this.customers.findIndex(c => c.id === id);
	}
}
